import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyIdProps,
  CompanyId,
  ErrorHandler,
} from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useEffect, useState } from "react";

type Props = ErrorHandler & BaseCompanyIdProps;

interface ReturnProps {
  /** The members of the workspace */
  workspaceMembers: FaroCoreApiTypes.AdmCompanyMemberWithAuth[];

  /** Indicator whether the workspace members are currently being fetched */
  isFetchingWorkspaceMembers: boolean;
}

/**
 * Use this hook to fetch the members of a workspace by providing companyId.
 * Returns the fetched members and a loading flag.
 * If the loading fails, the fetched members will be an empty array.
 */
export function useMembersOfWorkspace({
  companyId,
  errorHandler,
}: Props): ReturnProps {
  const [workspaceMembers, setWorkspaceMembers] = useState<
    FaroCoreApiTypes.AdmCompanyMemberWithAuth[]
  >([]);
  const [isFetchingWorkspaceMembers, setIsFetchingWorkspaceMembers] =
    useState<boolean>(true);

  // Fetches the workspace members
  useEffect(() => {
    async function fetchWorkspaceMembers(companyId: CompanyId): Promise<void> {
      setIsFetchingWorkspaceMembers(true);

      try {
        setWorkspaceMembers(
          await faroCoreApiClient.admin.getCompanyMembersById({
            companyId,
          })
        );
      } catch (error) {
        errorHandler({
          id: `use-members-of-workspace-${Date.now().toString()}`,
          title: "Could not load workspace members",
          error,
        });
        setWorkspaceMembers([]);
      } finally {
        setIsFetchingWorkspaceMembers(false);
      }
    }

    if (companyId) {
      void fetchWorkspaceMembers(companyId);
    }
  }, [companyId, errorHandler]);

  if (!companyId) {
    return { workspaceMembers: [], isFetchingWorkspaceMembers: false };
  }

  return { workspaceMembers, isFetchingWorkspaceMembers };
}
